import {
	PermissionEvaluation,
	PermissionFailureReason,
	PermissionPolicy,
	PermissionSet,
} from '../types';

interface ResourceWithPermissions {
	permissions?: PermissionSet;
}

export function checkForPermission(
	resource: ResourceWithPermissions,
	policy: PermissionPolicy
) {
	if (!resource.permissions?.[policy]) {
		return false;
	}

	const policyEvaluation = resource.permissions[policy];
	return Boolean(policyEvaluation?.enabled);
}

export function checkForAnyPermission(
	resource: ResourceWithPermissions,
	...policies: PermissionPolicy[]
) {
	return policies.some(policy => checkForPermission(resource, policy));
}

type ResourceResolvePermissionFailureReason<T extends ResourceWithPermissions> = (
	resource: T
) => boolean;

export class PermissionResolver<T extends ResourceWithPermissions> {
	private checks: {
		policy: PermissionPolicy;
		reason: PermissionFailureReason;
		shouldResolveDelegate: ResourceResolvePermissionFailureReason<T>;
	}[] = [];

	private constructor(private resource: T) {}

	public static create<T extends ResourceWithPermissions>(resource: T) {
		return new PermissionResolver(resource);
	}

	public tryResolvePermission(
		policy: PermissionPolicy,
		reason: PermissionFailureReason,
		shouldResolveDelegate: ResourceResolvePermissionFailureReason<T>
	) {
		this.checks.push({ policy, reason, shouldResolveDelegate });
		return this;
	}

	public computeUpdatedPermissions() {
		let currentResource = this.resource;
		for (const check of this.checks) {
			currentResource = resolvePermissionFailureReason(
				currentResource,
				check.policy,
				check.reason,
				check.shouldResolveDelegate
			);
		}
		return currentResource;
	}
}

function resolvePermissionFailureReason<T extends ResourceWithPermissions>(
	resource: T,
	policy: PermissionPolicy,
	reason: PermissionFailureReason,
	shouldResolveDelegate: (resource: T) => boolean
): T {
	if (!resource.permissions?.[policy]) {
		return resource;
	}

	const policyEvaluation = resource.permissions[policy];

	if (!policyEvaluation) {
		return resource;
	}

	let reasons = policyEvaluation.reasons ?? [];
	if (shouldResolveDelegate(resource)) {
		reasons = reasons.filter(x => x !== reason);
	} else {
		reasons.push(reason);
	}

	const newEvaluation: PermissionEvaluation =
		reasons.length === 0
			? { enabled: true, reasons: null! }
			: {
					enabled: false,
					reasons,
			  };

	return {
		...resource,
		permissions: {
			...resource.permissions,
			[policy]: newEvaluation,
		},
	};
}
